import { uiText } from '../../utils/i18n';
import { useMemo, useState, type ReactNode } from 'react';
import styles from './ConnectionsPanel.module.css';

export type ToolConnectionType =
  | 'git'
  | 'figma'
  | 'pencil'
  | 'notion'
  | 'email'
  | 'sql'
  | 's3'
  | 'guandata';

export interface ToolConnectionSummary {
  type: ToolConnectionType;
  botName: string;
  config: Record<string, unknown>;
  connected: true;
  lastTestedAt: number;
  createdAt: number;
  updatedAt: number;
}

interface ToolConnectionModalProps {
  type: ToolConnectionType;
  botName: string;
  token: string;
  name: string;
  icon: ReactNode;
  iconBg: string;
  existing?: ToolConnectionSummary;
  onClose: () => void;
  onChanged: (connection?: ToolConnectionSummary) => void;
}

const DEFAULTS: Record<ToolConnectionType, Record<string, string>> = {
  git: {
    remoteUrl: '',
    authMethod: 'https-token',
    username: '',
    token: '',
    privateKey: '',
    passphrase: '',
  },
  figma: {
    authMethod: 'personal-token',
    allowedFileKeys: '',
    token: '',
  },
  pencil: {
    workspaceSlug: '',
    cliKey: '',
    agentApiKey: '',
  },
  notion: {
    workspaceLabel: '',
    token: '',
  },
  email: {
    provider: 'gmail',
    host: 'imap.gmail.com',
    port: '993',
    secure: 'true',
    username: '',
    password: '',
    mailbox: 'INBOX',
    rejectUnauthorized: 'true',
  },
  sql: {
    dialect: 'postgresql',
    host: '',
    port: '5432',
    database: '',
    username: '',
    password: '',
    sslMode: 'require',
  },
  s3: {
    endpoint: '',
    region: 'cn-north-1',
    bucket: '',
    prefix: '',
    forcePathStyle: 'false',
    accessKeyId: '',
    secretAccessKey: '',
    sessionToken: '',
  },
  guandata: {
    baseUrl: '',
    domain: '',
    loginId: '',
    password: '',
  },
};

function existingForm(
  type: ToolConnectionType,
  existing?: ToolConnectionSummary,
): Record<string, string> {
  const form = { ...DEFAULTS[type] };
  if (!existing) return form;
  for (const [key, value] of Object.entries(existing.config)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      form[key] = String(value);
    } else if (Array.isArray(value)) {
      form[key] = value.join(', ');
    }
  }
  return form;
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  hint?: string;
}) {
  return (
    <div className={styles.formGroup}>
      <label className={styles.formLabel}>{label}</label>
      <input
        className={styles.formInput}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
      />
      {hint && <span className={styles.formHint}>{hint}</span>}
    </div>
  );
}

export function ToolConnectionModal({
  type,
  botName,
  token,
  name,
  icon,
  iconBg,
  existing,
  onClose,
  onChanged,
}: ToolConnectionModalProps) {
  const initialForm = useMemo(() => existingForm(type, existing), [type, existing]);
  const [form, setForm] = useState<Record<string, string>>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const set = (key: string, value: string) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'dialect') {
        const previousDefault = current.dialect === 'postgresql' ? '5432'
          : current.dialect === 'mysql' ? '3306' : '1433';
        if (!current.port || current.port === previousDefault) {
          next.port = value === 'postgresql' ? '5432' : value === 'mysql' ? '3306' : '1433';
        }
      }
      if (key === 'provider') {
        const hosts: Record<string, string> = {
          gmail: 'imap.gmail.com',
          outlook: 'outlook.office365.com',
          qq: 'imap.qq.com',
          '163': 'imap.163.com',
          custom: '',
        };
        next.host = hosts[value] || '';
        next.port = '993';
        next.secure = 'true';
      }
      return next;
    });
  };

  const payload = () => {
    if (type === 'git') {
      return {
        config: {
          remoteUrl: form.remoteUrl,
          authMethod: form.authMethod,
          username: form.username,
        },
        secrets: {
          token: form.token,
          privateKey: form.privateKey,
          passphrase: form.passphrase,
        },
      };
    }
    if (type === 'sql') {
      return {
        config: {
          dialect: form.dialect,
          host: form.host,
          port: Number(form.port),
          database: form.database,
          username: form.username,
          sslMode: form.sslMode,
        },
        secrets: { password: form.password },
      };
    }
    if (type === 'figma') {
      return {
        config: {
          authMethod: form.authMethod,
          allowedFileKeys: form.allowedFileKeys,
        },
        secrets: { token: form.token },
      };
    }
    if (type === 'pencil') {
      return {
        config: { workspaceSlug: form.workspaceSlug },
        secrets: { cliKey: form.cliKey, agentApiKey: form.agentApiKey },
      };
    }
    if (type === 'notion') {
      return {
        config: { workspaceLabel: form.workspaceLabel },
        secrets: { token: form.token },
      };
    }
    if (type === 'email') {
      return {
        config: {
          provider: form.provider,
          host: form.host,
          port: Number(form.port),
          secure: form.secure === 'true',
          username: form.username,
          mailbox: form.mailbox,
          rejectUnauthorized: form.rejectUnauthorized === 'true',
        },
        secrets: { password: form.password },
      };
    }
    if (type === 's3') {
      return {
        config: {
          endpoint: form.endpoint,
          region: form.region,
          bucket: form.bucket,
          prefix: form.prefix,
          forcePathStyle: form.forcePathStyle === 'true',
          accessKeyId: form.accessKeyId,
        },
        secrets: {
          secretAccessKey: form.secretAccessKey,
          sessionToken: form.sessionToken,
        },
      };
    }
    return {
      config: {
        baseUrl: form.baseUrl,
        domain: form.domain,
        loginId: form.loginId,
      },
      secrets: { password: form.password },
    };
  };

  const save = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/tool-connections/${type}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ botName, ...payload() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `连接失败 (${response.status})`);
      onChanged(data.connection);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '连接失败');
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/tool-connections/${type}?botName=${encodeURIComponent(botName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `断开失败 (${response.status})`);
      onChanged(undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '断开失败');
      setConfirmDisconnect(false);
    } finally {
      setLoading(false);
    }
  };

  const credentialHint = existing ? '留空会继续使用已加密保存的凭据' : undefined;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.toolModal}`} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalIconWrap} style={{ background: iconBg }}>{icon}</div>
          <div>
            <div className={styles.modalTitle}>{existing ? uiText("管理") : uiText("连接")}{name}</div>
            <div className={styles.modalSub}>{uiText("仅供")} {botName} {uiText("使用")}</div>
          </div>
          <div style={{ flex: 1 }} />
          <button className={styles.closeBtn} onClick={onClose} aria-label={uiText("关闭")}>&times;</button>
        </div>

        {success ? (
          <div className={styles.modalBody}>
            <div className={styles.successBox}>
              <span className={styles.successIcon}>&#10003;</span>
              <div>
                <div className={styles.successTitle}>{uiText("连接验证成功")}</div>
                <div className={styles.successDesc}>
                  {uiText("凭据已加密保存，")}{botName} {uiText("现在可以通过受限工具安全使用这个连接。")}
                </div>
              </div>
            </div>
            <button className={styles.primaryBtn} onClick={onClose}>{uiText("完成")}</button>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <div className={styles.securityHint}>
              <span>⌁</span>
              <div><b>{uiText("先验证，再保存")}</b><small>{uiText("密码、Token 和私钥只在服务端加密存储，不会写入 Agent 文件或对话。")}</small></div>
            </div>

            {type === 'git' && (
              <>
                <Field
                  label={uiText("仓库地址")}
                  value={form.remoteUrl}
                  onChange={(value) => set('remoteUrl', value)}
                  placeholder="https://github.com/org/repo.git"
                  hint={uiText("支持 GitHub、GitLab、Gitee 和企业内部 Git 服务")}
                />
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{uiText("认证方式")}</label>
                  <select className={styles.formInput} value={form.authMethod} onChange={(event) => set('authMethod', event.target.value)}>
                    <option value="https-token">HTTPS + Token</option>
                    <option value="ssh-key">{uiText("SSH 私钥")}</option>
                    <option value="public">{uiText("公开仓库（无需凭据）")}</option>
                  </select>
                </div>
                {form.authMethod === 'https-token' && (
                  <>
                    <Field
                      label={uiText("用户名")}
                      value={form.username}
                      onChange={(value) => set('username', value)}
                      placeholder={uiText("Git 用户名；GitHub App 可填 x-access-token")}
                    />
                    <Field
                      label={uiText("访问 Token")}
                      value={form.token}
                      onChange={(value) => set('token', value)}
                      placeholder={existing ? uiText("已保存，留空不修改") : 'Personal Access Token'}
                      type="password"
                      hint={credentialHint}
                    />
                  </>
                )}
                {form.authMethod === 'ssh-key' && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>{uiText("SSH 私钥")}</label>
                      <textarea
                        className={`${styles.formInput} ${styles.formTextarea}`}
                        value={form.privateKey}
                        onChange={(event) => set('privateKey', event.target.value)}
                        placeholder={existing ? uiText("已保存，留空不修改") : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                        spellCheck={false}
                      />
                      {credentialHint && <span className={styles.formHint}>{credentialHint}</span>}
                    </div>
                    <Field
                      label={uiText("私钥口令（可选）")}
                      value={form.passphrase}
                      onChange={(value) => set('passphrase', value)}
                      type="password"
                      hint={credentialHint}
                    />
                  </>
                )}
              </>
            )}

            {type === 'figma' && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{uiText("认证方式")}</label>
                  <select className={styles.formInput} value={form.authMethod} onChange={(event) => set('authMethod', event.target.value)}>
                    <option value="personal-token">Personal Access Token</option>
                    <option value="oauth-token">OAuth Access Token</option>
                  </select>
                </div>
                <Field
                  label={uiText("限定文件 Key（可选）")}
                  value={form.allowedFileKeys}
                  onChange={(value) => set('allowedFileKeys', value)}
                  placeholder="AbCdEf..., XyZ123..."
                  hint={uiText("多个文件用逗号分隔；留空时沿用 Token 自身可见范围")}
                />
                <Field
                  label="Figma Token"
                  value={form.token}
                  onChange={(value) => set('token', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("粘贴 Figma 访问 Token")}
                  type="password"
                  hint={credentialHint || uiText("建议只授予 current_user:read、file_content:read、file_comments:read")}
                />
                <div className={styles.hint}>{uiText("连接后可读取文件节点和评论，不开放修改设计、发表评论或管理团队。")}</div>
              </>
            )}

            {type === 'pencil' && (
              <>
                <Field
                  label={uiText("工作区标识（可选）")}
                  value={form.workspaceSlug}
                  onChange={(value) => set('workspaceSlug', value)}
                  placeholder={uiText("例如 product-design")}
                  hint={uiText("用于区分这个 Agent 的 Pencil 设计文件")}
                />
                <Field
                  label="Pencil CLI Key"
                  value={form.cliKey}
                  onChange={(value) => set('cliKey', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : 'PEN_CLI_KEY'}
                  type="password"
                  hint={credentialHint}
                />
                <Field
                  label="Codex API Key"
                  value={form.agentApiKey}
                  onChange={(value) => set('agentApiKey', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("供 Pencil 内的 Codex Agent 使用")}
                  type="password"
                  hint={credentialHint || uiText("作为 PEN_AGENT_API_KEY 仅传给本次本地 Pencil 进程")}
                />
                <div className={styles.hint}>{uiText("服务器会先验证 Pencil CLI Key。设计生成固定使用 Codex 5.6 sol · high，并只操作当前 Agent 工作区内的 .pen 文件。")}</div>
              </>
            )}

            {type === 'notion' && (
              <>
                <Field
                  label={uiText("工作区名称（可选）")}
                  value={form.workspaceLabel}
                  onChange={(value) => set('workspaceLabel', value)}
                  placeholder={uiText("例如 NovaX 产品团队")}
                />
                <Field
                  label="Notion Integration Token"
                  value={form.token}
                  onChange={(value) => set('token', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("secret_... 或 ntn_...")}
                  type="password"
                  hint={credentialHint || uiText("还需要在 Notion 中把目标页面分享给这个 Integration")}
                />
                <div className={styles.hint}>{uiText("连接后可搜索并读取已授权页面和内容块，不开放创建、修改或删除页面。")}</div>
              </>
            )}

            {type === 'email' && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{uiText("邮箱类型")}</label>
                  <select className={styles.formInput} value={form.provider} onChange={(event) => set('provider', event.target.value)}>
                    <option value="gmail">Gmail</option>
                    <option value="qq">{uiText("QQ 邮箱")}</option>
                    <option value="163">{uiText("163 邮箱")}</option>
                    <option value="custom">{uiText("企业邮箱 / 自定义 IMAP")}</option>
                  </select>
                </div>
                <div className={styles.formRow}>
                  <Field label={uiText("IMAP 地址")} value={form.host} onChange={(value) => set('host', value)} placeholder="imap.example.com" />
                  <Field label={uiText("端口")} value={form.port} onChange={(value) => set('port', value)} placeholder="993" type="number" />
                </div>
                <Field label={uiText("邮箱账号")} value={form.username} onChange={(value) => set('username', value)} placeholder="name@example.com" />
                <Field
                  label={uiText("应用专用密码 / 授权码")}
                  value={form.password}
                  onChange={(value) => set('password', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("请勿填写网页登录密码")}
                  type="password"
                  hint={credentialHint || uiText("Gmail、QQ、163 通常需要先开启 IMAP 并创建应用专用密码或授权码")}
                />
                <Field label={uiText("默认邮箱目录")} value={form.mailbox} onChange={(value) => set('mailbox', value)} placeholder="INBOX" />
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{uiText("TLS 加密")}</label>
                  <select className={styles.formInput} value={form.secure} onChange={(event) => set('secure', event.target.value)}>
                    <option value="true">{uiText("启用（推荐）")}</option>
                    <option value="false">{uiText("关闭")}</option>
                  </select>
                </div>
                <div className={styles.hint}>{uiText("全程以 IMAP 只读模式打开邮箱；可搜索和读取邮件正文、查看附件名称，不会发送、删除、移动或标记邮件。")}</div>
              </>
            )}

            {type === 'sql' && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{uiText("数据库类型")}</label>
                  <select className={styles.formInput} value={form.dialect} onChange={(event) => set('dialect', event.target.value)}>
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mysql">MySQL</option>
                    <option value="sqlserver">SQL Server</option>
                  </select>
                </div>
                <div className={styles.formRow}>
                  <Field label={uiText("数据库地址")} value={form.host} onChange={(value) => set('host', value)} placeholder="db.example.com" />
                  <Field label={uiText("端口")} value={form.port} onChange={(value) => set('port', value)} placeholder="5432" type="number" />
                </div>
                <Field label={uiText("数据库名")} value={form.database} onChange={(value) => set('database', value)} placeholder="analytics" />
                <Field label={uiText("账号")} value={form.username} onChange={(value) => set('username', value)} placeholder="readonly_user" />
                <Field
                  label={uiText("密码")}
                  value={form.password}
                  onChange={(value) => set('password', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("数据库密码")}
                  type="password"
                  hint={credentialHint || uiText("建议使用数据库侧的只读账号")}
                />
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>SSL</label>
                  <select className={styles.formInput} value={form.sslMode} onChange={(event) => set('sslMode', event.target.value)}>
                    <option value="require">{uiText("启用（兼容自签名证书）")}</option>
                    <option value="verify-full">{uiText("启用并验证证书")}</option>
                    <option value="disable">{uiText("关闭")}</option>
                  </select>
                </div>
              </>
            )}

            {type === 's3' && (
              <>
                <Field
                  label={uiText("S3 兼容地址（AWS S3 可留空）")}
                  value={form.endpoint}
                  onChange={(value) => set('endpoint', value)}
                  placeholder="https://s3.example.com"
                  hint={uiText("兼容 AWS S3、MinIO 及大多数 S3 协议对象存储")}
                />
                <div className={styles.formRow}>
                  <Field label={uiText("区域")} value={form.region} onChange={(value) => set('region', value)} placeholder="cn-north-1" />
                  <Field label="Bucket" value={form.bucket} onChange={(value) => set('bucket', value)} placeholder="company-data" />
                </div>
                <Field label={uiText("限定前缀（可选）")} value={form.prefix} onChange={(value) => set('prefix', value)} placeholder="reports/" />
                <Field label="Access Key ID" value={form.accessKeyId} onChange={(value) => set('accessKeyId', value)} />
                <Field
                  label="Secret Access Key"
                  value={form.secretAccessKey}
                  onChange={(value) => set('secretAccessKey', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : 'Secret Access Key'}
                  type="password"
                  hint={credentialHint}
                />
                <Field
                  label={uiText("Session Token（可选）")}
                  value={form.sessionToken}
                  onChange={(value) => set('sessionToken', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("临时凭据才需要")}
                  type="password"
                  hint={credentialHint}
                />
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{uiText("寻址方式")}</label>
                  <select className={styles.formInput} value={form.forcePathStyle} onChange={(event) => set('forcePathStyle', event.target.value)}>
                    <option value="false">{uiText("虚拟主机样式（AWS 默认）")}</option>
                    <option value="true">{uiText("路径样式（MinIO 常用）")}</option>
                  </select>
                </div>
                <div className={styles.hint}>{uiText("连接后可列出对象并读取限定前缀内不超过 2MB 的文件，不开放上传、覆盖或删除。")}</div>
              </>
            )}

            {type === 'guandata' && (
              <>
                <Field
                  label={uiText("观远 BI 地址")}
                  value={form.baseUrl}
                  onChange={(value) => set('baseUrl', value)}
                  placeholder="https://bi.example.com"
                />
                <Field label="Domain" value={form.domain} onChange={(value) => set('domain', value)} placeholder={uiText("例如 demo")} />
                <Field label={uiText("登录账号")} value={form.loginId} onChange={(value) => set('loginId', value)} placeholder="name@example.com" />
                <Field
                  label={uiText("密码")}
                  value={form.password}
                  onChange={(value) => set('password', value)}
                  placeholder={existing ? uiText("已保存，留空不修改") : uiText("观远 BI 密码")}
                  type="password"
                  hint={credentialHint}
                />
                <div className={styles.hint}>
                  {uiText("基于观远官方 GuanCLI 验证连接。连接后可读取页面、卡片、数据集、ETL、指标与 ChatBI，默认不开放创建、修改和删除。")}
                </div>
              </>
            )}

            {error && <div className={styles.errorMsg}>{error}</div>}

            <button className={styles.primaryBtn} onClick={save} disabled={loading}>
              {loading ? uiText("正在验证连接…") : existing ? uiText("验证并保存") : uiText("验证并连接")}
            </button>

            {existing && (
              <div className={styles.disconnectArea}>
                {!confirmDisconnect ? (
                  <button className={styles.disconnectBtn} onClick={() => setConfirmDisconnect(true)} disabled={loading}>{uiText("断开连接")}</button>
                ) : (
                  <div className={styles.disconnectConfirm}>
                    <span>{uiText("确定断开？已保存凭据会被删除。")}</span>
                    <div>
                      <button onClick={() => setConfirmDisconnect(false)} disabled={loading}>{uiText("取消")}</button>
                      <button className={styles.disconnectConfirmBtn} onClick={disconnect} disabled={loading}>{uiText("确认断开")}</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
